
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Menu, X } from "lucide-react";
import { useAuth } from "@/contexts/auth";
import { usePermissions } from "@/hooks/use-permissions";
import { useIsMobile } from "@/hooks/use-mobile";
import AdminHeader from "@/components/admin/AdminHeader";
import AdminSidebar from "@/components/admin/AdminSidebar";
import { IdentityDocsManagement } from "@/components/admin/identity-docs/IdentityDocsManagement";
import { Button } from "@/components/ui/button";

const AdminIdentityDocs = () => {
  const navigate = useNavigate();
  const { user, profile, isLoading } = useAuth();
  const { isSuperAdmin, isAdmin } = usePermissions();
  const isMobile = useIsMobile();
  const [sidebarOpen, setSidebarOpen] = useState(!isMobile);
  const [authorized, setAuthorized] = useState(false);
  
  useEffect(() => {
    // Set document title
    document.title = "Identity Documents - NationCard";
    
    // Check permissions on component mount
    if (!isLoading) {
      if (!user) {
        toast.error("Please log in to access the admin dashboard");
        navigate("/auth");
        return;
      }
      
      if (!isAdmin() && !isSuperAdmin()) {
        toast.error("You do not have permission to access the admin dashboard");
        navigate("/");
        return;
      }
      
      setAuthorized(true);
    }
  }, [user, profile, isAdmin, isSuperAdmin, navigate, isLoading]);
  
  // Effect to handle sidebar state on mobile/desktop switch
  useEffect(() => {
    setSidebarOpen(!isMobile);
  }, [isMobile]);
  
  if (isLoading) {
    return <div className="flex h-screen w-full items-center justify-center">
      <div className="text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-nation-blue mx-auto mb-4"></div>
        <p className="text-nation-blue">Loading admin dashboard...</p>
      </div>
    </div>;
  }
  
  if (!authorized && !isLoading) {
    return null;
  }
  
  return (
    <div className="flex min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Mobile sidebar overlay */}
      {isMobile && sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40"
          onClick={() => setSidebarOpen(false)}
        />
      )}
      
      {/* Sidebar with conditional display for mobile */}
      <div 
        className={`${
          sidebarOpen ? "translate-x-0" : "-translate-x-full"
        } fixed inset-y-0 left-0 z-50 w-64 bg-white dark:bg-gray-800 border-r border-gray-200 dark:border-gray-700 transform transition-transform duration-200 ease-in-out md:relative md:translate-x-0`}
      >
        <div className="h-full flex flex-col">
          {isMobile && (
            <div className="p-4 flex justify-end">
              <Button 
                variant="ghost" 
                size="icon" 
                onClick={() => setSidebarOpen(false)} 
                aria-label="Close sidebar"
              >
                <X className="h-5 w-5" />
              </Button>
            </div>
          )}
          <AdminSidebar onClose={() => isMobile && setSidebarOpen(false)} />
        </div>
      </div>
      
      {/* Main content */}
      <div className="flex-1 flex flex-col min-w-0">
        <AdminHeader>
          <Button
            variant="ghost"
            size="icon"
            className="mr-2 md:hidden"
            onClick={() => setSidebarOpen(true)}
          >
            <Menu className="h-5 w-5" />
          </Button>
          Identity Documents
        </AdminHeader>
        
        <main className="p-3 md:p-4 lg:p-6 flex-1 overflow-hidden">
          <div className="mb-3 md:mb-4">
            <div className="text-xs md:text-sm text-gray-500 mb-1">
              Admin / Identity Documents
            </div>
          </div>
          
          <div className="w-full max-w-full">
            <IdentityDocsManagement />
          </div>
        </main>
      </div>
    </div>
  );
};

export default AdminIdentityDocs;
